// orderSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getAllOrders,
  getUserOrders,
  createRaistComplaint,
  createRequestRevision,
  getOrderDetailsByAdmin,
  getAvailableAdminTeamMembers,
  addAdminTeamMembers,
  addMilestoneNoteByAdmin,
  getOrderCounts,
  startMilestone,
  submitMilestone,
  startRevision,
  submitRevision,
  startOrderRevision,
  submitOrderRevision,
  addOrderNote,
  addAdminComment,
  approveMilestone,
} from "../thunks/orderThunks";

import { getThreadDetailsByOrder } from "../thunks/threadThunks";

const orderSlice = createSlice({
  name: "order",
  initialState: {
    orders: [],
    loading: false,
    error: null,
    pagination: {},
    filters: {},
    infoMessage: null,
    // Order counts for tabs
    counts: {
      all: 0,
      completed: 0,
      revision: 0,
      inProgress: 0,
    },
    countsLoading: false,
    adminOrderDetails: {
      data: null,
      loading: false,
      error: null,
    },
    thread: {
      data: null,
      loading: false,
      error: null,
    },
    availableAdminTeamMembers: {
      data: [],
      loading: false,
      error: null,
    },
    orderNote: {
      loading: false,
      error: null,
      success: false,
    },
    adminComment: {
      loading: false,
      error: null,
      success: false,
    },
  },

  reducers: {
    clearOrders: (state) => {
      state.orders = [];
      state.error = null;
    },
    setOrdersLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearAdminOrderDetails: (state) => {
      state.adminOrderDetails = {
        data: null,
        loading: false,
        error: null,
      };
    },
    clearAvailableAdminTeamMembers: (state) => {
      state.availableAdminTeamMembers = {
        data: [],
        loading: false,
        error: null,
      };
    },
    resetAdminCommentState: (state) => {
      state.adminComment = {
        loading: false,
        error: null,
        success: false,
      };
    },
    // Thread-related reducers
    setOrderChat: (state, action) => {
      state.thread.data = action.payload;
    },
    updateLastMessage: (state, action) => {
      const { lastMessage } = action.payload;
      if (state.thread.data) {
        // Normalize sender name if missing
        if (lastMessage?.sender && !lastMessage.sender.name) {
          const { firstName, lastName } = lastMessage.sender;
          if (firstName || lastName) {
            lastMessage.sender.name = `${firstName || ""} ${
              lastName || ""
            }`.trim();
          }
        }
        state.thread.data.lastMessage = lastMessage;
      }
    },
    updateLastMessageDeliveredToAll: (state, action) => {
      const { isDeliveredToAll } = action.payload;
      if (state.thread.data) {
        state.thread.data.isLastMessageDeliveredToAll = isDeliveredToAll;
      }
    },
    updateLastMessageReadByAll: (state, action) => {
      const { isReadByAll } = action.payload;
      if (state.thread.data) {
        state.thread.data.isLastMessageReadByAll = isReadByAll;
      }
    },
    participantJoined: (state, action) => {
      const { threadId, user, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        // Prevent duplicates
        const exists = state.thread.data.participants.some(
          (p) => p._id === user._id,
        );
        if (!exists) {
          state.thread.data.participants.push(user);
        }
        state.thread.data.isGroup = isGroup;
      }
    },
    participantLeft: (state, action) => {
      const { threadId, userId, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        state.thread.data.participants = state.thread.data.participants.filter(
          (p) => p._id !== userId,
        );
        state.thread.data.isGroup = isGroup;
      }
    },
    updateThreadInfo: (state, action) => {
      const { threadId, name, isCustomName, isGroup } = action.payload;
      if (state.thread.data && state.thread.data._id === threadId) {
        state.thread.data.name = name;
        state.thread.data.isCustomName = isCustomName;
        state.thread.data.isGroup = isGroup;
      }
    },
    clearOrderChat: (state) => {
      state.thread.data = null;
    },
  },
  extraReducers(builder) {
    // Get All Orders
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.infoMessage =
          action.payload.message ===
          "Currently you are not assigned to any order"
            ? action.payload.message
            : null;
        state.orders = action.payload.data.orders || [];
        state.pagination = action.payload.data.pagination || {};
        state.filters = action.payload.data.filters || {};
      })
      .addCase(getAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Get Order Counts
    builder
      .addCase(getOrderCounts.pending, (state) => {
        state.countsLoading = true;
      })
      .addCase(getOrderCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.counts = action.payload.counts || {
          all: 0,
          completed: 0,
          revision: 0,
          inProgress: 0,
        };
      })
      .addCase(getOrderCounts.rejected, (state, action) => {
        state.countsLoading = false;
      });

    // Get User Orders
    builder
      .addCase(getUserOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders || [];
        state.pagination = action.payload.pagination || {};
      })
      .addCase(getUserOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Create Request Revision
    builder
      .addCase(createRequestRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRequestRevision.fulfilled, (state, action) => {
        state.loading = false;
        // Update order status and milestone if current order matches
        const data = action.payload?.data;
        if (
          state.adminOrderDetails.data &&
          data?.orderId === state.adminOrderDetails.data._id
        ) {
          state.adminOrderDetails.data.status = "IN_REVISION";

          if (
            state.adminOrderDetails.data.progress?.milestoneProgress &&
            data.milestoneId
          ) {
            const mIndex =
              state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
                (m) => m._id === data.milestoneId,
              );
            if (mIndex !== -1) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                mIndex
              ].progressStatus = "REVISION_REQUESTED";
            }
          }
        }
      })
      .addCase(createRequestRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      });
    builder
      .addCase(getOrderDetailsByAdmin.pending, (state) => {
        state.adminOrderDetails.loading = true;
        state.adminOrderDetails.error = null;
      })
      .addCase(getOrderDetailsByAdmin.fulfilled, (state, action) => {
        state.adminOrderDetails.loading = false;
        state.adminOrderDetails.data = action.payload.data;
      })
      .addCase(getOrderDetailsByAdmin.rejected, (state, action) => {
        state.adminOrderDetails.loading = false;
        state.adminOrderDetails.error =
          action.payload?.message || "Failed to fetch order details for admin";
      });

    builder
      .addCase(addOrderNote.pending, (state) => {
        state.orderNote.loading = true;
        state.orderNote.error = null;
        state.orderNote.success = false;
      })
      .addCase(addOrderNote.fulfilled, (state, action) => {
        state.orderNote.loading = false;
        state.orderNote.success = true;

        // Update order details with new comment
        if (state.adminOrderDetails.data) {
          // Add new comment to orderComments array (at the beginning for latest first)
          const newComment = action.payload.data.comment;

          // Initialize if doesn't exist
          if (!state.adminOrderDetails.data.comments) {
            state.adminOrderDetails.data.comments = {};
          }
          if (!state.adminOrderDetails.data.comments.orderComments) {
            state.adminOrderDetails.data.comments.orderComments = [];
          }

          // Add new comment at the beginning
          state.adminOrderDetails.data.comments.orderComments.unshift(
            newComment,
          );

          // Update total comment count
          state.adminOrderDetails.data.comments.totalCommentCount =
            (state.adminOrderDetails.data.comments.totalCommentCount || 0) + 1;

          // Set hasUnreadActivity to true
          state.adminOrderDetails.data.comments.hasUnreadActivity = true;
        }
      })
      .addCase(addOrderNote.rejected, (state, action) => {
        state.orderNote.loading = false;
        state.orderNote.error =
          action.payload?.message || "Failed to add order note";
        state.orderNote.success = false;
      });

    // Add Admin Comment
    builder
      .addCase(addAdminComment.pending, (state) => {
        state.adminComment.loading = true;
        state.adminComment.error = null;
        state.adminComment.success = false;
      })
      .addCase(addAdminComment.fulfilled, (state, action) => {
        state.adminComment.loading = false;
        state.adminComment.success = true;

        // Update order details with new admin comment
        if (state.adminOrderDetails.data) {
          // Based on your handleResponse structure, data is in action.payload.data
          const responseData = action.payload?.data;

          if (!responseData) {
            console.error("No data in response payload");
            return;
          }

          const { comment, orderId, totalComments } = responseData;

          if (!comment) {
            console.error("No comment in response data:", responseData);
            return;
          }

          // Initialize comments structure if doesn't exist
          if (!state.adminOrderDetails.data.comments) {
            state.adminOrderDetails.data.comments = {};
          }
          if (!state.adminOrderDetails.data.comments.orderComments) {
            state.adminOrderDetails.data.comments.orderComments = [];
          }

          // Helper function to add comment to nested structure
          const addCommentToTree = (comments, newComment) => {
            // If no parent, add as root comment
            if (!newComment.parentCommentId) {
              return [newComment, ...comments];
            }

            // Find parent and add as reply
            const updatedComments = comments.map((commentItem) => {
              if (commentItem.id === newComment.parentCommentId) {
                const updatedReplies = [
                  ...(commentItem.replies || []),
                  newComment,
                ];

                return {
                  ...commentItem,
                  replies: updatedReplies,
                  replyCount: (commentItem.replyCount || 0) + 1,
                  lastActivityAt: newComment.lastActivityAt,
                };
              }

              // Recursively check replies
              if (commentItem.replies && commentItem.replies.length > 0) {
                return {
                  ...commentItem,
                  replies: addCommentToTree(commentItem.replies, newComment),
                };
              }

              return commentItem;
            });

            return updatedComments;
          };

          // Get existing ADMIN_COMMENT comments
          const existingAdminComments =
            state.adminOrderDetails.data.comments.orderComments.filter(
              (c) => c && c.category === "ADMIN_COMMENT",
            );

          // Get other comments (non-admin)
          const otherComments =
            state.adminOrderDetails.data.comments.orderComments.filter(
              (c) => c && c.category !== "ADMIN_COMMENT",
            );

          // Add new admin comment to the tree
          const updatedAdminComments = addCommentToTree(
            existingAdminComments,
            comment,
          );

          // Combine all comments back
          state.adminOrderDetails.data.comments.orderComments = [
            ...otherComments,
            ...updatedAdminComments,
          ];

          // Update total comment count
          if (totalComments !== undefined) {
            state.adminOrderDetails.data.comments.totalCommentCount =
              totalComments;
          }
          state.adminOrderDetails.data.comments.hasUnreadActivity = true;

          // Update last activity timestamp
          if (comment.lastActivityAt) {
            state.adminOrderDetails.data.lastActivityAt =
              comment.lastActivityAt;
          }
        }
      })
      .addCase(addAdminComment.rejected, (state, action) => {
        state.adminComment.loading = false;
        state.adminComment.error =
          action.payload?.message || "Failed to add admin comment";
        state.adminComment.success = false;
      });

    builder
      .addCase(getAvailableAdminTeamMembers.pending, (state) => {
        state.availableAdminTeamMembers.loading = true;
        state.availableAdminTeamMembers.error = null;
      })
      .addCase(getAvailableAdminTeamMembers.fulfilled, (state, action) => {
        state.availableAdminTeamMembers.loading = false;
        state.availableAdminTeamMembers.data =
          action.payload.data?.availableAdminMembers || [];
      })
      .addCase(getAvailableAdminTeamMembers.rejected, (state, action) => {
        state.availableAdminTeamMembers.loading = false;
        state.availableAdminTeamMembers.error =
          action.payload?.message ||
          "Failed to fetch available admin team members";
      });

    // Add Admin Team Members
    builder
      .addCase(addAdminTeamMembers.pending, (state) => {
        // You can add loading state here if needed
      })
      .addCase(addAdminTeamMembers.fulfilled, (state, action) => {
        // Success handled in component - we'll refresh order details
      })
      .addCase(addAdminTeamMembers.rejected, (state, action) => {
        // Error handled in component
      });

    builder
      .addCase(addMilestoneNoteByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMilestoneNoteByAdmin.fulfilled, (state, action) => {
        state.loading = false;

        if (state.adminOrderDetails?.data?.progress?.milestoneProgress) {
          // Find and update the milestone with new comment
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) => milestone._id === action.payload.milestoneId,
            );

          if (milestoneIndex !== -1) {
            // Initialize comments array if not exists
            if (
              !state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].comments
            ) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].comments = [];
            }

            // Helper to recursively find parent and add reply
            const addReplyToParent = (commentsList, newComment) => {
              for (const comment of commentsList) {
                if (
                  comment._id === newComment.parentCommentId ||
                  comment.id === newComment.parentCommentId
                ) {
                  if (!comment.replies) comment.replies = [];
                  comment.replies.push(newComment);
                  return true;
                }
                if (
                  comment.replies &&
                  addReplyToParent(comment.replies, newComment)
                ) {
                  return true;
                }
              }
              return false;
            };

            const comments =
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].comments;
            const newComment = action.payload.comment;

            if (newComment.parentCommentId) {
              // Add as reply
              addReplyToParent(comments, newComment);
            } else {
              // Add as root comment (at the top)
              comments.unshift(newComment);
            }

            // Update comment count
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ]._commentCount = action.payload.milestoneCommentCount;

            // Update hasUnreadComments
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].hasUnreadComments = action.payload.milestoneHasUnreadComments;
          }

          // Update total comment count
          if (state.adminOrderDetails.data.comments) {
            state.adminOrderDetails.data.comments.totalCommentCount =
              action.payload.totalOrderComments;
            state.adminOrderDetails.data.comments.hasUnreadActivity =
              action.payload.orderHasUnreadActivity;
          }
        }
      })
      .addCase(addMilestoneNoteByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // // Raise complaint order
    // builder.addCase(createRaistComplaint.fulfilled, (state, action) => {
    //     // Handle complaint creation if needed
    //   });

    //   // Create request revision
    // builder.addCase(createRequestRevision.fulfilled, (state, action) => {
    //     // Handle revision creation if needed
    //   });

    //   // Raise complaint order
    // builder.addCase(createRaistComplaint.fulfilled, (state, action) => {
    //     // Handle complaint creation if needed
    //   });

    //   // Create request revision
    // builder.addCase(createRequestRevision.fulfilled, (state, action) => {
    //     // Handle revision creation if needed
    //   });
    builder
      .addCase(startMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startMilestone.fulfilled, (state, action) => {
        state.loading = false;

        if (state.adminOrderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            // Update timeline
            if (action.payload.data.milestone.timeline) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline = {
                ...state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].timeline,
                ...action.payload.data.milestone.timeline,
              };
            }

            // Update startedAt date
            if (action.payload.data.milestone.startedAt) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].inProgressAt = action.payload.data.milestone.startedAt;
            }
          }

          // Update order status if changed
          if (action.payload.data.order?.status) {
            state.adminOrderDetails.data.status =
              action.payload.data.order.status;
          }

          // Update order current milestone
          if (action.payload.data.order.currentMilestone) {
            state.adminOrderDetails.data.progress.currentMilestone =
              action.payload.data.order.currentMilestone;
          }
        }
      })
      .addCase(startMilestone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Approve Milestone (Admin)
    builder
      .addCase(approveMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveMilestone.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data;

        if (
          state.adminOrderDetails?.data?.progress?.milestoneProgress &&
          data?.milestone
        ) {
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) => milestone._id === data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone properties
            const milestone =
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ];

            // Update status
            if (data.milestone.progressStatus) {
              milestone.progressStatus = data.milestone.progressStatus;
            }
            // Also update top-level status if present (MilestonePhase often uses 'status' property)
            if (data.milestone.status) {
              milestone.status = data.milestone.status;
            }

            // Update other fields if present
            if (data.milestone.amount) milestone.amount = data.milestone.amount;
            if (data.milestone.deadline)
              milestone.deadline = data.milestone.deadline;
            if (data.milestone.description)
              milestone.description = data.milestone.description;
            if (data.milestone.notes) milestone.notes = data.milestone.notes;
            if (data.milestone.phase) milestone.phase = data.milestone.phase; // Title update
            if (data.milestone.attachments)
              milestone.attachments = data.milestone.attachments;
            if (data.milestone.splitPayments)
              milestone.splitPayments = data.milestone.splitPayments;
          }

          // Update order status if changed
          if (data.order?.status) {
            state.adminOrderDetails.data.status = data.order.status;
          }
        }
      })
      .addCase(approveMilestone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Submit Milestone (Admin)
    builder
      .addCase(submitMilestone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitMilestone.fulfilled, (state, action) => {
        state.loading = false;

        if (state.adminOrderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            // Update timeline
            if (action.payload.data.milestone.timeline) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline = {
                ...state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].timeline,
                ...action.payload.data.milestone.timeline,
              };
            }

            // Update delivery notes and attachments
            if (action.payload.data.milestone.deliveryNotes) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].deliveryNotes = action.payload.data.milestone.deliveryNotes;
            }

            // Add new attachments
            if (action.payload.data.milestone.attachments) {
              const existingAttachments =
                state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].attachments || [];
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].attachments = [
                ...existingAttachments,
                ...action.payload.data.milestone.attachments,
              ];
            }

            // Update deliveredAt date
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].deliveredAt = action.payload.data.milestone.deliveredAt;
          }

          // Update order status and timeline
          if (action.payload.data.order.status) {
            state.adminOrderDetails.data.status =
              action.payload.data.order.status;
          }

          if (action.payload.data.order.timeline) {
            state.adminOrderDetails.data.timeline = {
              ...state.adminOrderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }

          // Update current milestone
          if (action.payload.data.order.currentMilestone) {
            state.adminOrderDetails.data.progress.currentMilestone =
              action.payload.data.order.currentMilestone;
          }
        }
      })
      .addCase(submitMilestone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Start Revision (Admin)
    builder
      .addCase(startRevision.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload?.data;
        if (!data) return;

        // Handle response with 'revision' object
        if (data.revision && data.revision.milestoneId) {
          if (state.adminOrderDetails?.data?.progress?.milestoneProgress) {
            const milestoneIndex =
              state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
                (m) => m._id === data.revision.milestoneId,
              );

            if (milestoneIndex !== -1) {
              const milestone =
                state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ];

              // Update status and stage
              milestone.progressStatus = "IN_REVISION";
              milestone.revisionStage = "IN_REVISION";
              milestone.inRevisionAt = data.revision.requestedAt;

              // Update revision count if available
              if (data.revision.revisionNumber) {
                milestone.revisionCount = data.revision.revisionNumber;
              }
            }
          }
        }
        // Fallback for legacy response format (contain milestone object)
        else if (
          data.milestone &&
          state.adminOrderDetails?.data?.progress?.milestoneProgress
        ) {
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) => milestone._id === data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = data.milestone.progressStatus;

            // Update timeline
            if (data.milestone.timeline) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].timeline = {
                ...state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].timeline,
                ...data.milestone.timeline,
              };
            }

            // Update revision flags
            if (data.milestone.revisionRequested !== undefined) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].revisionRequested = data.milestone.revisionRequested;
            }

            // Update inRevisionAt date
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].inRevisionAt = data.milestone.inRevisionAt;
          }

          // Update order status and timeline if present
          if (data.order && data.order.status) {
            state.adminOrderDetails.data.status = data.order.status;
          }

          if (data.order && data.order.timeline) {
            state.adminOrderDetails.data.timeline = {
              ...state.adminOrderDetails.data.timeline,
              ...data.order.timeline,
            };
          }
        }
      })
      .addCase(startRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Submit Revision (Admin)
    builder
      .addCase(submitRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitRevision.fulfilled, (state, action) => {
        state.loading = false;

        if (state.adminOrderDetails?.data?.progress?.milestoneProgress) {
          const milestoneIndex =
            state.adminOrderDetails.data.progress.milestoneProgress.findIndex(
              (milestone) =>
                milestone._id === action.payload.data.milestone._id,
            );

          if (milestoneIndex !== -1) {
            // Update milestone progress status back to DELIVERED
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].progressStatus = action.payload.data.milestone.progressStatus;

            // Update timeline - clear revision entries
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].timeline = {
              notStarted:
                state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].timeline?.notStarted,
              inProgress:
                state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].timeline?.inProgress,
              delivered: action.payload.data.milestone.deliveredAt,
              // Clear revision timeline entries
              accepted: null,
              completed: null,
              revisionRequested: null,
              inRevision: null,
            };

            // Update delivery notes (append revision notes)
            if (action.payload.data.milestone.deliveryNotes) {
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].deliveryNotes = action.payload.data.milestone.deliveryNotes;
            }

            // Add new revision delivery attachments
            if (action.payload.data.milestone.attachments) {
              const existingAttachments =
                state.adminOrderDetails.data.progress.milestoneProgress[
                  milestoneIndex
                ].attachments || [];
              state.adminOrderDetails.data.progress.milestoneProgress[
                milestoneIndex
              ].attachments = [
                ...existingAttachments,
                ...action.payload.data.milestone.attachments,
              ];
            }

            // Update revision flags
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionRequested = false;
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionNotes = "";

            // Update deliveredAt date
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].deliveredAt = action.payload.data.milestone.deliveredAt;

            // Clear revision timeline dates
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].revisionRequestedAt = null;
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].inRevisionAt = null;
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].acceptedAt = null;
            state.adminOrderDetails.data.progress.milestoneProgress[
              milestoneIndex
            ].completedAt = null;
          }

          // Update order status back to DELIVERED
          if (action.payload.data.order.status) {
            state.adminOrderDetails.data.status =
              action.payload.data.order.status;
          }

          // Update order timeline
          if (action.payload.data.order.timeline) {
            state.adminOrderDetails.data.timeline = {
              ...state.adminOrderDetails.data.timeline,
              ...action.payload.data.order.timeline,
            };
          }

          // Clear revisionRequested flag from order timeline
          state.adminOrderDetails.data.timeline.revisionRequested = null;
          state.adminOrderDetails.data.timeline.inRevision = null;
        }
      })
      .addCase(submitRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Start Order-level Revision (Admin)
    builder
      .addCase(startOrderRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startOrderRevision.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data;
        if (!data) return;

        if (state.adminOrderDetails?.data) {
          state.adminOrderDetails.data.status = "IN_REVISION";
          state.adminOrderDetails.data.timeline = {
            ...state.adminOrderDetails.data.timeline,
            inRevision: data.revision?.requestedAt || new Date(),
          };
          if (data.revision?.revisionNumber) {
            state.adminOrderDetails.data.revisionCount =
              data.revision.revisionNumber;
          }
        }
      })
      .addCase(startOrderRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Submit Order-level Revision (Admin)
    builder
      .addCase(submitOrderRevision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitOrderRevision.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data;
        if (!data) return;

        if (state.adminOrderDetails?.data) {
          state.adminOrderDetails.data.status = "DELIVERED";
          state.adminOrderDetails.data.timeline = {
            ...state.adminOrderDetails.data.timeline,
            delivered: data.revision?.deliveredAt || new Date(),
            inRevision: null,
            revisionRequested: null,
          };
        }
      })
      .addCase(submitOrderRevision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Thread Details By Order
    builder
      .addCase(getThreadDetailsByOrder.pending, (state) => {
        state.thread.loading = true;
        state.thread.error = null;
      })
      .addCase(getThreadDetailsByOrder.fulfilled, (state, action) => {
        state.thread.loading = false;
        const { thread } = action.payload.data;
        state.thread.data = thread;
      })
      .addCase(getThreadDetailsByOrder.rejected, (state, action) => {
        state.thread.loading = false;
        state.thread.error = action.payload || "Failed to fetch thread details";
      });
  },
});

export const {
  clearOrders,
  setOrdersLoading,
  clearAdminOrderDetails,
  clearAvailableAdminTeamMembers,
  resetMilestoneState,
  resetOrderNoteState,
  updateMilestoneStatus,
  clearMilestoneError,
  resetAdminCommentState,
  setOrderChat,
  updateLastMessage,
  updateLastMessageDeliveredToAll,
  updateLastMessageReadByAll,
  participantJoined,
  participantLeft,
  updateThreadInfo,
  clearOrderChat,
} = orderSlice.actions;
export default orderSlice.reducer;
